import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ConfirmationDialogComponent } from '@app/components';
import { DataFieldTypes } from '@app/enums/data-field-types.enum';
import { isSameModel } from '@app/functions/is-same-model';
import { DataFieldGroup } from '@app/models/settings/data-field-group.model';
import { DataFieldOption } from '@app/models/settings/data-field-option.model';
import { AuthService, NotifyService } from '@app/services';
import { BaseForm } from '@forms/base.form';
import { Role } from '@models/account/role.model';
import { DataField } from '@models/settings/data-field.model';
import { DataFieldOptionStateManager } from '@settings/classes/data-field-option-state-manager.class';
import { RoleNameDisplayOptions } from '@app/types/translatables/role-display.options';

@Component({
    selector: 'app-field-details-form',
    templateUrl: './field-details.template.html',
    styleUrls: ['./field-details.style.scss'],
})
export class FieldDetailsForm extends BaseForm implements OnInit {
    @Input() field: DataField = new DataField();
    @Input() group: DataFieldGroup = new DataFieldGroup();
    @Input() dataFieldOptionState: DataFieldOptionStateManager;
    @Input() showFieldGroup = true;
    @Input() disableFieldType = false;
    @Input() preselectDefaultAdmin = false;
    @Output() groupChange: EventEmitter<DataFieldGroup> = new EventEmitter<DataFieldGroup>();
    @ViewChild('confirmRemoveDialog') confirmRemoveDialog: ConfirmationDialogComponent;

    compareModels = isSameModel;

    roleNameDisplay = RoleNameDisplayOptions;
    fieldGroups: DataFieldGroup[] = [];
    newFieldGroup: DataFieldGroup;
    allRoles: Role[] = [];
    // customRoles[] and defaultRoles[] are used for custom sorting
    customRoles: Role[] = [];
    defaultRoles: Role[] = [];
    selectableRoles: Role[] = [];
    viewOnlySelectableRoles: Role[] = [];
    roleIds = [];
    viewOnlyRoleIds = [];
    hasMinMax = false;
    placeholderInputDisabled = false;
    dataFieldTypes = DataFieldTypes;

    isLoading = false;

    constructor(
        private auth: AuthService,
        private notify: NotifyService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        await this.getDataFieldGroups();

        this.newFieldGroup = new DataFieldGroup();
        const [roles] = await Role.where('company_id', this.auth.company.id).orderBy('name', 'ASC').all();
        this.customRoles = roles.filter((role) => !role.isImmutable);
        this.defaultRoles = [
            ...roles.filter((role) => role.name === 'Admin'),
            ...roles.filter((role) => role.name === 'Manager'),
            ...roles.filter((role) => role.name === 'Employee'),
        ];
        this.allRoles = [...this.defaultRoles, ...this.customRoles];

        if (this.preselectDefaultAdmin) {
            this.roleIds = roles.filter((role) => role.name === 'Admin').map((role) => role.id);
        } else {
            this.roleIds = this.field.roles.map((role) => role.id);
        }

        this.viewOnlyRoleIds = this.field.viewOnlyRoles.map((role) => role.id);
        this.selectableRoles = this.allRoles.filter((role) => !this.viewOnlyRoleIds.includes(role.id));
        this.viewOnlySelectableRoles = this.allRoles.filter((role) => !this.roleIds.includes(role.id));

        if (this.field.type !== null) {
            this.disablePlaceholderInputForDropdownField(this.field.type);
        }

        this.hasMinMax = this.typeHasMinMax(this.field.type);

        this.isLoading = false;
    }

    getEmptyDataFieldOption(): DataFieldOption {
        return new DataFieldOption();
    }

    onValidationTypeSelect(): void {
        this.hasMinMax = this.typeHasMinMax(this.field.type);
        this.disablePlaceholderInputForDropdownField(this.field.type);
    }

    onGroupChange(): void {
        this.groupChange.emit(this.group);
    }

    onRoleChange(roles: Role[]): void {
        this.roleIds = roles;
        this.field.roleIds = this.roleIds;
        this.viewOnlySelectableRoles = this.allRoles.filter((role) => !this.roleIds.includes(role.id));
    }

    onViewOnlyRoleChange(viewOnlyRoles: Role[]): void {
        this.viewOnlyRoleIds = viewOnlyRoles;
        this.field.viewOnlyRoleIds = this.viewOnlyRoleIds;
        this.selectableRoles = this.allRoles.filter((role) => !this.viewOnlyRoleIds.includes(role.id));
    }

    async onOptionRemove(dataFieldOptionIndex: number): Promise<void> {
        if (this.dataFieldOptionState.shouldConfirmBeforeRemoving(dataFieldOptionIndex)) {
            const shouldRemove = await this.confirmRemoveDialog.open();
            if (!shouldRemove) {
                return;
            }
        }

        this.dataFieldOptionState.removeDataFieldOption(dataFieldOptionIndex);
    }

    private disablePlaceholderInputForDropdownField(type: string): void {
        const defaultDropdownPlaceholder = 'Select an option';

        if (type === DataFieldTypes.dropdown) {
            this.field.description = defaultDropdownPlaceholder;
            this.placeholderInputDisabled = true;
            return;
        }

        if (type !== DataFieldTypes.dropdown && this.field.description === defaultDropdownPlaceholder) {
            this.field.description = '';
            this.placeholderInputDisabled = false;
        }
    }

    private async getDataFieldGroups(): Promise<void> {
        try {
            const [customFieldGroups] = await DataFieldGroup.param('company', this.auth.company.id)
                .where('onlyCustomGroups', true)
                .orderBy('name', 'ASC')
                .all();

            const [defaultFieldGroups] = await DataFieldGroup.param('company', this.auth.company.id)
                .where('onlyDefaultGroups', true)
                .all();

            this.fieldGroups = [...defaultFieldGroups, ...customFieldGroups];
        } catch (_) {
            this.notify.error('Could not get data field groups');
        }
    }

    /*
     * Should we display mix/max inputs during create and edit
     * NOTE: We don't want to let users to edit min/max for long-text fields
     */
    private typeHasMinMax(type: string): boolean {
        return type === DataFieldTypes.shortText || type === DataFieldTypes.number;
    }
}
